import { CloseOutlined } from '@ant-design/icons';
import { Alert, Button, Checkbox, Space, Tag, Typography } from 'antd';
import React from 'react';
import type { CompanyEnrichmentField } from '@/services/bff/types/customer';
import styles from './company-enrichment-toolbar.module.less';
import type { CompanyEnrichmentFeedback } from './use-company-enrichment';

const LABELS: Record<CompanyEnrichmentField, string> = {
  fullName: '企业全称',
  unifiedSocialCreditCode: '信用代码',
  legalRepresentative: '法定代表人',
  registeredCapital: '注册资本',
  establishmentDate: '成立时间',
  website: '官网',
  remark: '描述',
  telephone: '电话',
  email: '邮箱',
  qichachaUrl: '企查查网址',
  tianyanchaUrl: '天眼查网址',
};
export function CompanyEnrichmentToolbar({
  loading,
  disabled,
  onSupplement,
  feedback,
  onConfirm,
  onSelectionChange,
  onDismiss,
}: {
  loading: boolean;
  disabled: boolean;
  onSupplement: () => void;
  feedback?: CompanyEnrichmentFeedback;
  onConfirm: (fields?: CompanyEnrichmentField[]) => void;
  onSelectionChange: (
    fields: CompanyEnrichmentField[],
    selected: boolean,
  ) => void;
  onDismiss: () => void;
}) {
  const suggestions = feedback?.suggestions || [];
  const selected = suggestions.filter((item) => item.selected).length;
  const fields = suggestions.map((item) => item.field);
  return (
    <section className={styles.root} aria-label="AI 企业信息建议">
      <Space wrap style={{ marginBottom: 12 }}>
        <Button loading={loading} disabled={disabled} onClick={onSupplement}>
          AI 补全企业信息
        </Button>
        <Typography.Text type="secondary">
          根据企业名称或信用代码查询，确认后填入表单
        </Typography.Text>
      </Space>
      {feedback && (
        <>
          <Alert
            style={{ marginBottom: 12 }}
            type={
              feedback.error || feedback.result?.status !== 'matched'
                ? 'warning'
                : 'info'
            }
            showIcon
            closable={{
              closeIcon: <CloseOutlined />,
              'aria-label': '关闭建议区',
            }}
            onClose={onDismiss}
            title={
              feedback.error ||
              (feedback.result?.status === 'matched'
                ? `待确认 ${suggestions.length} 项，已确认 ${feedback.count} 项`
                : '未获得可确认的企业信息，请完善查询信息')
            }
            description={
              <Space orientation="vertical" size={4}>
                <span>
                  AI
                  建议可能有误，请核对当前值、建议值及来源。确认后仍需保存客户。
                </span>
                {feedback.result?.status === 'matched' &&
                  !suggestions.length &&
                  !feedback.count && <span>没有可补充或替换的不同信息</span>}
                {[...new Set(feedback.result?.warnings || [])].map(
                  (warning) => (
                    <span key={warning}>{warning}</span>
                  ),
                )}
              </Space>
            }
          />
          {suggestions.length > 0 && (
            <>
              <Space wrap style={{ marginBottom: 8 }}>
                <Checkbox
                  checked={selected === suggestions.length}
                  indeterminate={selected > 0 && selected < suggestions.length}
                  disabled={disabled}
                  onChange={(event) =>
                    onSelectionChange(fields, event.target.checked)
                  }
                >
                  全选
                </Checkbox>
                <Button
                  type="text"
                  disabled={disabled || !selected}
                  onClick={() => onSelectionChange(fields, false)}
                >
                  取消全选
                </Button>
                <Button
                  type="primary"
                  disabled={disabled || !selected}
                  onClick={() => onConfirm()}
                >
                  确认所选（{selected}）
                </Button>
              </Space>
              <ul className={styles.list} aria-label="待确认字段">
                {suggestions.map((item) => (
                  <li key={item.field} className={styles.row}>
                    <Checkbox
                      aria-label={`选择${LABELS[item.field]}`}
                      checked={item.selected}
                      disabled={disabled}
                      onChange={(event) =>
                        onSelectionChange([item.field], event.target.checked)
                      }
                    />
                    <div className={styles.field}>
                      <Typography.Text strong>
                        {LABELS[item.field]}
                      </Typography.Text>
                      {item.currentValue && (
                        <Tag color="warning">将替换已有内容</Tag>
                      )}
                    </div>
                    <div className={styles.value}>
                      <Typography.Text type="secondary">当前值</Typography.Text>
                      <div>{item.currentValue || '空'}</div>
                    </div>
                    <div className={styles.value}>
                      <Typography.Text type="secondary">
                        AI 建议值
                      </Typography.Text>
                      <div>{item.value}</div>
                      <Typography.Link
                        href={item.source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.source.title || item.source.url}
                      </Typography.Link>
                    </div>
                    <Button
                      aria-label={`确认${LABELS[item.field]}`}
                      disabled={disabled}
                      onClick={() => onConfirm([item.field])}
                    >
                      确认
                    </Button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </section>
  );
}
